import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from './adminApi';
import {
  TrendingUp,
  Users,
  Inbox,
  Flame,
  Target,
  RefreshCw,
} from 'lucide-react';
import { TIER_META, STATUS_META } from './AdminDashboard';

/**
 * Tiny CSS-only chart — renders two overlaid bar series with a shared y-axis.
 * Keeps the bundle size small (no recharts/d3) while looking premium.
 */
const TimeseriesChart = ({ points = [] }) => {
  const max = useMemo(
    () => Math.max(1, ...points.map((p) => Math.max(p.leads || 0, p.subscribers || 0))),
    [points]
  );

  return (
    <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6" data-testid="analytics-timeseries">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white text-base font-medium font-['Inter']">Activity over time</h2>
          <p className="text-white/40 text-xs font-['Lato'] mt-1">
            Daily new leads and newsletter subscribers
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs font-['Inter']">
          <span className="flex items-center gap-1.5 text-[#83BF42]">
            <span className="w-3 h-3 bg-[#83BF42] rounded-sm" /> Leads
          </span>
          <span className="flex items-center gap-1.5 text-blue-400">
            <span className="w-3 h-3 bg-blue-400 rounded-sm" /> Subscribers
          </span>
        </div>
      </div>

      <div
        className="grid items-end gap-1 h-48"
        style={{ gridTemplateColumns: `repeat(${Math.max(points.length, 1)}, minmax(0, 1fr))` }}
      >
        {points.map((p, i) => {
          const lh = ((p.leads || 0) / max) * 100;
          const sh = ((p.subscribers || 0) / max) * 100;
          return (
            <div
              key={p.date || i}
              data-testid={`analytics-bar-${i}`}
              className="relative h-full flex items-end justify-center group"
              title={`${p.date}: ${p.leads || 0} leads, ${p.subscribers || 0} subscribers`}
            >
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-[2px] h-full items-end w-[80%]">
                <div
                  style={{ height: `${lh}%` }}
                  className="flex-1 bg-[#83BF42]/80 rounded-t-sm min-h-[2px] group-hover:bg-[#83BF42] transition-colors"
                />
                <div
                  style={{ height: `${sh}%` }}
                  className="flex-1 bg-blue-400/70 rounded-t-sm min-h-[2px] group-hover:bg-blue-400 transition-colors"
                />
              </div>
              <div className="absolute -bottom-5 text-[9px] text-white/30 font-['Inter'] whitespace-nowrap rotate-[-30deg] origin-top">
                {i % Math.max(1, Math.floor(points.length / 10)) === 0 && (p.date || '').slice(5)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-[#15151A] border border-white/5 rounded-2xl p-5">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-4 ${accent}`}>
      <Icon className="w-4 h-4" />
    </div>
    <p className="text-white text-3xl font-light font-['Inter']">{value}</p>
    <p className="text-white/40 text-xs uppercase tracking-wider font-['Inter'] mt-1">{label}</p>
    {sub && <p className="text-[#83BF42] text-xs font-['Lato'] mt-2">{sub}</p>}
  </div>
);

const DistributionRow = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div data-testid={`distribution-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-white/70 text-xs font-['Inter'] uppercase tracking-wider">{label}</span>
        <span className="text-white/50 text-xs font-['Lato']">{count} · {pct}%</span>
      </div>
      <div className="w-full bg-white/[0.04] rounded-full h-1.5 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const AdminAnalytics = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async (d = days) => {
    setLoading(true);
    try {
      const res = await adminApi.analytics(d);
      setData(res);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = data?.totals || {};
  const tierTotal = useMemo(
    () => Object.values(data?.tier_distribution || {}).reduce((a, b) => a + b, 0),
    [data]
  );
  const statusTotal = useMemo(
    () => Object.values(data?.status_distribution || {}).reduce((a, b) => a + b, 0),
    [data]
  );

  return (
    <div className="px-8 lg:px-12 py-10" data-testid="admin-analytics-page">
      <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">Reporting</p>
          <h1 className="text-white text-4xl font-light font-['Inter']">Analytics</h1>
          <p className="text-white/40 text-sm font-['Lato'] mt-2">
            Time-series, lead-tier breakdown and pipeline health.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5" data-testid="analytics-window-picker">
            {[7, 30, 90, 180].map((d) => (
              <button
                key={d}
                onClick={() => { setDays(d); refresh(d); }}
                data-testid={`analytics-window-${d}`}
                className={`px-3 py-1.5 rounded-full text-xs font-['Inter'] transition ${
                  days === d ? 'bg-[#83BF42] text-[#0F0F11]' : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            onClick={() => refresh(days)}
            data-testid="analytics-refresh"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-white/60 text-xs font-['Inter'] hover:bg-white/5 transition"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Top stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat
              icon={Inbox}
              label={`Leads (${days}d)`}
              value={totals.leads_window ?? 0}
              sub={`${totals.leads_all_time ?? 0} all time`}
              accent="bg-[#83BF42]/10 text-[#83BF42]"
            />
            <Stat
              icon={Users}
              label={`Subscribers (${days}d)`}
              value={totals.subscribers_window ?? 0}
              sub={`${totals.subscribers_all_time ?? 0} all time`}
              accent="bg-blue-500/10 text-blue-400"
            />
            <Stat
              icon={Flame}
              label="Hot leads (window)"
              value={data?.tier_distribution?.hot ?? 0}
              sub={tierTotal > 0 ? `${Math.round(((data?.tier_distribution?.hot ?? 0) / tierTotal) * 100)}% of recent` : null}
              accent="bg-red-500/10 text-red-400"
            />
            <Stat
              icon={Target}
              label="Won leads"
              value={data?.status_distribution?.won ?? 0}
              sub={`${data?.status_distribution?.qualified ?? 0} qualified · ${data?.status_distribution?.lost ?? 0} lost`}
              accent="bg-emerald-500/10 text-emerald-400"
            />
          </div>

          {/* Timeseries chart */}
          <div className="mb-6">
            <TimeseriesChart points={data?.timeseries || []} />
          </div>

          {/* Distributions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6" data-testid="status-distribution">
              <h3 className="text-white text-sm font-medium font-['Inter'] mb-5 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#83BF42]" /> Pipeline
              </h3>
              <div className="space-y-4">
                {Object.entries(data?.status_distribution || {}).map(([s, c]) => {
                  const meta = STATUS_META[s] || STATUS_META.new;
                  const colorClass = {
                    new: 'bg-[#83BF42]',
                    contacted: 'bg-blue-400',
                    qualified: 'bg-purple-400',
                    won: 'bg-emerald-400',
                    lost: 'bg-red-400',
                  }[s] || 'bg-white/40';
                  return (
                    <DistributionRow
                      key={s}
                      label={meta.label}
                      count={c}
                      total={statusTotal}
                      color={colorClass}
                    />
                  );
                })}
              </div>
            </div>

            <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6" data-testid="tier-distribution">
              <h3 className="text-white text-sm font-medium font-['Inter'] mb-5 flex items-center gap-2">
                <Flame className="w-4 h-4 text-[#83BF42]" /> Lead heat
              </h3>
              <div className="space-y-4">
                {['hot', 'warm', 'cool', 'cold'].map((t) => (
                  <DistributionRow
                    key={t}
                    label={TIER_META[t].label}
                    count={data?.tier_distribution?.[t] ?? 0}
                    total={tierTotal}
                    color={
                      { hot: 'bg-red-400', warm: 'bg-orange-400', cool: 'bg-blue-400', cold: 'bg-white/30' }[t]
                    }
                  />
                ))}
              </div>
            </div>

            <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6" data-testid="top-sources">
              <h3 className="text-white text-sm font-medium font-['Inter'] mb-5 flex items-center gap-2">
                <Target className="w-4 h-4 text-[#83BF42]" /> Top sources
              </h3>
              <div className="space-y-3">
                {(data?.top_sources || []).length === 0 ? (
                  <p className="text-white/30 text-xs font-['Lato']">No data yet.</p>
                ) : (
                  (data?.top_sources || []).map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-sm font-['Inter']">
                      <span className="text-white/70 truncate flex-1 mr-3">{s.source}</span>
                      <span className="text-[#83BF42] tabular-nums">{s.count}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
